/**
 * Auth Service - аутентификация и авторизация
 */

import { apiClient } from "../client";
import { ENDPOINTS } from "../endpoints";
import {
    clearStoredSession,
    invalidateStoredSession,
} from "../sessionEvents";
import { browserName, osName } from "react-device-detect";

// Получить IP (для логина)
const getIp = async () => {
    try {
        const data = await apiClient.get(
            "https://api.ipify.org?format=json",
            {},
            false,
        );
        return data?.ip || "unknown";
    } catch {
        return "unknown";
    }
};

const isWhirlpoolHash = (value) => {
    if (typeof value !== "string") return false;
    return /^[a-f0-9]{128}$/i.test(value);
};

export const authService = {
    /**
     * Логин пользователя
     */
    login: async (login, password) => {
        const ip = await getIp();
        const data = {
            login,
            password,
            ip,
            client: browserName,
            platform: osName,
        };
        return apiClient.postPublic(ENDPOINTS.AUTH.LOGIN, data);
    },

    /**
     * Подтверждение 2FA (legacy gauth)
     */
    confirmAuth: async ({
        user_name,
        user_password,
        ip,
        client,
        platform,
        verification_code,
    }) => {
        return apiClient.postPublic(ENDPOINTS.AUTH.CONFIRM_AUTH, {
            user_name,
            user_password,
            ip,
            client,
            platform,
            verification_code,
        });
    },

    /**
     * Проверка 2FA кода
     */
    checkTwoFactor: async (code) => {
        return apiClient.post(
            ENDPOINTS.TWO_FACTOR.CHECK,
            { code },
            true,
            false,
        );
    },

    /**
     * Настройка 2FA
     */
    setupTwoFactor: async () => {
        return apiClient.get(ENDPOINTS.TWO_FACTOR.SETUP, {}, true);
    },

    /**
     * Получить IP клиента
     */
    getIp: getIp,

    /**
     * Сохранение данных пользователя после логина
     */
    saveUserData: (data) => {
        const token =
            data?.jwt ||
            data?.token ||
            data?.jwtToken ||
            data?.access_token ||
            data?.accessToken;
        if (token) {
            localStorage.setItem("jwtToken", token);
        }

        // Some legacy endpoints return a "player" object, others return flat user fields
        const player = data?.player || data;
        if (player?.id) {
            localStorage.setItem("user-id", player.id);
        }
        if (player?.login) {
            localStorage.setItem("user-login", player.login);
        }
        if (player?.password) {
            localStorage.setItem("user-password", player.password);
        }
        if (player?.email) {
            localStorage.setItem("user-email", player.email);
        }
    },

    /**
     * Очистка данных пользователя (логаут)
     */
    clearUserData: () => {
        clearStoredSession();
    },

    invalidateSession: () => {
        invalidateStoredSession();
    },

    /**
     * Нормализация сохранённой авторизации (миграция/защита от старых данных).
     * Возвращает true, если пришлось очистить данные.
     */
    sanitizeStoredAuth: () => {
        const token = localStorage.getItem("jwtToken");
        const id = localStorage.getItem("user-id");
        const login = localStorage.getItem("user-login");
        const password = localStorage.getItem("user-password");

        // If any legacy credentials exist without a token, they can break legacy UCP calls
        if (!token && (id || login || password)) {
            authService.clearUserData();
            return true;
        }

        // If token exists but required legacy fields are missing/invalid, force re-login
        if (token) {
            if (!id || !login || !password) {
                authService.clearUserData();
                return true;
            }
            // Old versions could store plain password (e.g. 2FA step) — backend expects hash
            if (!isWhirlpoolHash(password)) {
                authService.clearUserData();
                return true;
            }
        }

        return false;
    },

    /**
     * Проверка авторизации
     */
    isAuthenticated: () => {
        const token = localStorage.getItem("jwtToken");
        if (!token) {
            return false;
        }

        const id = localStorage.getItem("user-id");
        const login = localStorage.getItem("user-login");
        const password = localStorage.getItem("user-password");

        return !!(
            id &&
            login &&
            password &&
            isWhirlpoolHash(password)
        );
    },

    /**
     * Получить текущего пользователя
     */
    getCurrentUser: () => {
        const id = localStorage.getItem("user-id");
        const login = localStorage.getItem("user-login");
        const email = localStorage.getItem("user-email");

        if (!id) return null;

        return { id, login, email };
    },
};

export default authService;
